import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Page from "@insite/mobius/Page";
import * as React from "react";
import { ReactNode } from "react";
import styled from "styled-components";

/* Husky Custom Code - Start */
import GlassBoxExternalScript from "@husky-hrc/Components/Injectors/GlassBoxExternalScript";
import OneTrustScript from "@husky-hrc/Components/Injectors/OneTrustScript";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";
/* Husky Custom Code - End */

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

/* Husky Custom Code - Start */
export interface PageStyles {
    breadcrumbs?: InjectableCss;
}

const desktopBreadcrumbWrapper = css`
    max-width: none;
    position: relative;
    z-index: 1;
    top: -3px;
`;

export const pageStyles: PageStyles = {
    breadcrumbs: {
        css: css`
            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: none;
                    `,
                    css`
                        max-width: none;
                    `,
                    desktopBreadcrumbWrapper,
                    desktopBreadcrumbWrapper,
                    desktopBreadcrumbWrapper,
                ]);
            }}
        `,
    },
};

const styles = pageStyles;
/* Husky Custom Code - End */

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    showSignInOverlay,
    signInOverlay,
    showStorePickupOverlay,
    storePickupOverlay,
    showAddressBookOverlay,
    addressBookOverlay,
}: PageLayoutProps) => {
    /* Husky Custom Code - Start */
    const firstRender = React.useRef(true);
    const shellContext = React.useContext(ShellContext);

    React.useEffect(() => {
        if (!shellContext.isInShell) {
            if (firstRender.current) {
                firstRender.current = false;
            } else {
                const element = [...document.querySelectorAll("[data-id='PageTitle']")].find(
                    element => !!(element as HTMLElement).offsetParent,
                );
                (element as HTMLElement)?.focus();
            }
        }
    }, [pageContent]);
    /* Husky Custom Code - End */

    return (
        <PageLayoutWrapper>
            {/* Husky Custom Code - Start */}
            <OneTrustScript />
            <GlassBoxExternalScript />
            {/* Husky Custom Code - End */}
            {showHeader && header}
            {/* Husky Removed - showCompactHeader && compactHeader */}
            {showBreadcrumbs && (
                <Page as="div" {...styles.breadcrumbs} /* Husky Custom Code */>
                    <PageBreadcrumbs />
                </Page>
            )}
            <div tabIndex={-1} data-test-selector="mainContentAndFooter" /* Husky Custom Code */>
                {pageContent}
                {showFooter && footer}
            </div>
            {showSignInOverlay && signInOverlay}
            {showStorePickupOverlay && storePickupOverlay}
            {showAddressBookOverlay && addressBookOverlay}
        </PageLayoutWrapper>
    );
};

const PageLayoutWrapper = styled.div`
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    > main {
        flex: 1 1 0%;
    }

    @media print {
        display: block;
    }
`;

export default PageLayout;
